import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RefreshCw, Search, X } from 'lucide-react';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import { reasons } from '../components/ReservationInbox';
import { useReservationAlert } from '../components/ReservationAlertContext';
import { alsDatum, datumUndZeit, tagSchluessel, tagUeberschrift, uhrzeit } from '../lib/zeit';
import './Reservations.css';

// ─────────────────────────────────────────────────────────────────────────────
// RESERVIERUNG-KOMPLETT-001 (Owner 16.10.2026): „Reservierung muss eigenen Reiter bekommen, nicht
// unter Fahrten, und besser gesteuert werden.“ Vorlage: ~/Voogo/VORSCHAU-RESERVIERUNGEN-REITER.html.
//
// Daten: GET /admin/reservation-inbox?view=board (nur lesend). Die Einordnung je Reservierung
// (Kachel, Schrittleiste, Hinweise) kommt fertig vom Server (lib/reservationBoard.js) — der Zähler
// am Menüpunkt nutzt dieselbe Funktion. Aktionen laufen über die bestehenden Routen
// /admin/reservation-inbox/{candidates,assign,driver-candidates,assign-driver}; keine neuen Geldwege.
// ─────────────────────────────────────────────────────────────────────────────

export const POLL_MS = 20000;

const CLASSES = { standard: 'VOOGO Basis', comfort: 'VOOGO Komfort', xl: 'VOOGO Family', premium: 'VOOGO Business', max: 'VOOGO Van', taxi: 'VOOGO Taxi' };
const METHODS = { cash: 'Bar', card: 'Karte', paypal: 'PayPal' };
const PAYMENTS = { pending: 'offen', paid: 'bezahlt', authorized: 'autorisiert', collection_failed: 'fehlgeschlagen', failed: 'fehlgeschlagen' };
// RESERVIERUNG-KOMPLETT-001: Ursache einer Absage nach Annahme (reservation_provider_cancel_cause).
const CANCEL_CAUSE = {
  driver_release: 'Fahrer hat freigegeben', driver_reject: 'Fahrer hat abgelehnt', offline: 'Fahrer war zum Termin nicht online',
  ineligible: 'Fahrer/Fahrzeug nicht mehr freigegeben', unreachable: 'Fahrer schafft den Termin nicht', hard_gate: 'zu kurz vor Abholung',
};
const ACTION_ERRORS = {
  reminder_too_soon: 'Der Fahrer wurde gerade erst erinnert. Nächste Erinnerung in frühestens 10 Minuten.',
  no_live_driver_offer: 'Diese Reservierung ist gerade keinem Fahrer angeboten.',
  open_market_not_allowed: 'Diese Reservierung kann gerade nicht für alle freigegeben werden.',
};
const CANCELLED_BY = { customer: 'Kunde', driver: 'Fahrer', admin: 'Admin', partner: 'Partner', system: 'System' };

export const TILES = [
  { key: 'need', label: 'Braucht Sie jetzt' },
  { key: 'wait', label: 'Wartet auf Annahme' },
  { key: 'ok', label: 'Fahrer fest' },
  { key: 'market', label: 'Offen für alle' },
  { key: 'done', label: 'Erledigt / storniert (7 Tage)' },
];

const money = (value) => Number(value || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
const shortId = (id) => String(id || '').slice(0, 8);
const ms = (value) => alsDatum(value)?.getTime() ?? NaN;

// „noch 3 Std. 5 Min.“ / „vor 4 Min.“
export function restzeit(value, nowMs = Date.now()) {
  const target = ms(value);
  if (!Number.isFinite(target)) return '';
  const diff = Math.round((target - nowMs) / 60000);
  const abs = Math.abs(diff);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const text = h ? `${h} Std.${m ? ` ${m} Min.` : ''}` : `${m} Min.`;
  return diff >= 0 ? `noch ${text}` : `vor ${text}`;
}

const inText = (value, nowMs) => {
  const target = ms(value);
  if (!Number.isFinite(target)) return '';
  const diff = Math.round((target - nowMs) / 60000);
  if (diff < 0) return `vor ${restzeit(value, nowMs).replace('vor ', '')}`;
  if (diff < 24 * 60) return `in ${restzeit(value, nowMs).replace('noch ', '')}`;
  return datumUndZeit(value, { jahr: false });
};

// Eine Zeile je Schritt; „angenommen“ steht nur bei der Stufe, die wirklich angenommen hat.
export function stepText(step, nowMs = Date.now()) {
  const t = (value) => uhrzeit(value, { leer: '–' });
  switch (`${step.key}:${step.kind}`) {
    case 'booked:booked': return `Gebucht${step.at ? ` ${datumUndZeit(step.at, { jahr: false })}` : ''}`;
    case 'partner:awaiting_admin': return step.state === 'fail' ? 'Partner: Abholzeit abgelaufen – Entscheidung erforderlich' : 'Partner: wartet auf Ihre Zuweisung';
    case 'partner:open_market': return `Offen für alle${step.since ? ` seit ${t(step.since)}` : ''} · alle Partner und Fahrer, wer zuerst annimmt`;
    case 'partner:offered': return `Partner: ${step.name} angeboten · Annahme offen bis ${t(step.until)} (${restzeit(step.until, nowMs)})`;
    case 'partner:offer_missed': return `Partner: ${step.name} hat die Frist verpasst (${t(step.until)})`;
    case 'partner:accepted': return `Partner: ${step.name} hat angenommen`;
    case 'partner:via_driver': return `Partner: ${step.name ? `${step.name} · ` : ''}über seinen Fahrer (Systemvermittlung)`;
    case 'partner:admin_direct': return `Partner: ${step.name} · Admin hat den Fahrer direkt gewählt`;
    case 'partner:open_market_driver': return `Partner: ${step.name} · sein Fahrer hat sie aus „offen für alle“ übernommen`;
    case 'partner:none': return 'Partner: keine aktuelle Zuweisung';
    case 'driver:confirmed': return `Fahrer: ${step.name} · fest`;
    case 'driver:offered': return `Fahrer: ${step.name} · hat noch nicht angenommen · Frist ${t(step.until)} (${restzeit(step.until, nowMs)})`;
    case 'driver:offer_missed': return `Fahrer: ${step.name} hat nicht angenommen · Frist ${t(step.until)} abgelaufen`;
    case 'driver:system_offer': return `Fahrer: System fragt ${step.name} an · Antwort bis ${t(step.until)}`;
    case 'driver:market': return 'Fahrer: alle passenden Fahrer sehen sie · noch niemand angenommen';
    case 'driver:fleet': return `Fahrer: an alle Fahrer von ${step.name} freigegeben · noch niemand angenommen`;
    case 'driver:partner_must_choose': return `Fahrer: ${step.name} muss noch einen Fahrer wählen`;
    case 'driver:live_search': return 'Fahrer: normale Fahrtsuche läuft';
    case 'driver:none': return 'Fahrer: noch keiner';
    case 'pickup:planned': return `Abholung ${t(step.at)}`;
    case 'pickup:live_search': return 'In normale Fahrtsuche umgewandelt';
    case 'pickup:en_route': return 'Fahrer unterwegs zum Abholort';
    case 'pickup:arrived': return 'Fahrer am Abholort';
    case 'pickup:riding': return 'Fahrt läuft';
    case 'pickup:completed': return `Erledigt${step.at ? ` ${datumUndZeit(step.at, { jahr: false })}` : ''}`;
    case 'pickup:cancelled': return `Storniert${step.by ? ` von ${CANCELLED_BY[step.by] || step.by}` : ''}${step.at ? ` · ${datumUndZeit(step.at, { jahr: false })}` : ''}`;
    case 'pickup:no_driver': return 'Kein Fahrer gefunden';
    case 'pickup:expired': return `Automatisch beendet: kein Fahrer bis 30 Min. nach Abholzeit${step.at ? ` · ${datumUndZeit(step.at, { jahr: false })}` : ''}`;
    default: return step.key;
  }
}
const ICON = { done: '✓', open: '⏳', fail: '✕', todo: '○' };

export function attentionText(code, row, rules) {
  switch (code) {
    case 'awaiting_admin': {
      const after = Number(rules?.open_market_after_min) || 30;
      const latest = Number(rules?.open_market_latest_before_min) || 45;
      const received = ms(row.reservation_admin_received_at);
      const pickup = ms(row.planned_for);
      // HAUPTFADEN-ESSEN-220 H-3: Essen-Verträge gehen nicht „an alle“, sondern automatisch an einen Partner.
      const essen = row.reservation_tariff?.version === 'pulse-essen-admin-receipt-v1';
      if (essen) {
        // Nie „erledigt das System“ behaupten, wenn es gerade nicht klappt (Prüffall, kein Partner, Zeit vorbei).
        if (row.reservation_tariff.receipt_review_required || row.reservation_tariff.review_open) return 'Wartet auf Ihre Zuweisung · PULSE-Essen: bitte prüfen';
        // Die Automatik endet 5 Min. vor der Angebotsgrenze T−10 (keine Angebote mit < 5 Min. Restlaufzeit).
        if (pickup - ((Number(rules?.hard_gate_before_min) || 10) + 5) * 60000 <= Date.now()) return 'Wartet auf Ihre Zuweisung · PULSE-Essen: bitte selbst zuweisen';
        if (row.reservation_tariff.required_profile === 'ESSEN_MINIMUM') return 'Wartet auf Ihre Zuweisung · PULSE-Essen: wird automatisch einem Partner angeboten, sobald einer passt';
        const essenLatest = Number(rules?.essen_auto_latest_before_min) || 75;
        const autoAt = Math.min(received + after * 60000, pickup - essenLatest * 60000);
        if (!Number.isFinite(autoAt)) return 'Wartet auf Ihre Zuweisung';
        return autoAt > Date.now()
          ? `Wartet auf Ihre Zuweisung · PULSE-Essen: ohne Zuweisung ab ${uhrzeit(autoAt)} automatisch einem Partner angeboten`
          : 'Wartet auf Ihre Zuweisung · PULSE-Essen: noch kein passender Partner – das System versucht es weiter';
      }
      const openAt = Math.min(received + after * 60000, pickup - latest * 60000);
      return Number.isFinite(openAt)
        ? `Wartet auf Ihre Zuweisung · ohne Zuweisung ab ${uhrzeit(openAt)} für alle Partner und Fahrer offen`
        : 'Wartet auf Ihre Zuweisung';
    }
    case 'pickup_expired': return 'Abholzeit abgelaufen – bitte entscheiden';
    case 'partner_offer_missed': return row?.reservation_tariff?.version === 'pulse-essen-admin-receipt-v1'
      ? 'Partnerfrist abgelaufen · neu zuweisen'
      : 'Partnerfrist abgelaufen · neu zuweisen (sonst bietet das System den nächsten Partner an)';
    // HAUPTFADEN-ESSEN-220 F2: Zustand der automatischen Essen-Rotation, unterscheidbar.
    case 'essen_offer_paused': {
      const next = ms(row?.essen_rotation?.next_offer_at);
      if (!Number.isFinite(next)) return 'PULSE-Essen: nächstes automatisches Angebot folgt';
      const today = new Date(next).toDateString() === new Date().toDateString();
      return `PULSE-Essen: nächstes automatisches Angebot ab ${today ? uhrzeit(next) : datumUndZeit(next, { jahr: false })}`;
    }
    case 'essen_offer_budget_exhausted': return 'PULSE-Essen: automatische Angebote aufgebraucht – bitte selbst zuweisen';
    case 'essen_history_full': return 'PULSE-Essen: Übergabeverlauf voll – keine Übergabe an andere Betriebe mehr; die angenommene Firma kann noch zuweisen, sonst nur Storno';
    case 'driver_offer_missed': return 'Fahrer hat nicht rechtzeitig angenommen';
    case 'no_assignment': return 'Keine aktuelle Zuweisung · bitte zuweisen';
    case 'uncovered': return 'Als ungedeckt gemeldet · Live-Suche läuft';
    case 'no_driver_soon': return 'Abholung in weniger als 45 Min. und noch kein Fahrer fest';
    case 'provider_cancelled': {
      const essen = row.reservation_tariff?.version === 'pulse-essen-admin-receipt-v1';
      const next = row.open_market ? '; sie ist jetzt offen für alle Partner und Fahrer'
        : essen ? '; PULSE-Essen: bitte selbst zuweisen' : '; spätestens 45 Min. vor Abholung ist sie für alle offen';
      return `Absage – Suche läuft · ${CANCEL_CAUSE[row.provider_cancel_cause] || 'Fahrer hat abgegeben'}${row.provider_cancelled_at ? ` um ${uhrzeit(row.provider_cancelled_at)}` : ''}. Der Kunde zahlt dafür nichts${next}.`;
    }
    case 'possible_duplicate': return `Mögliche Doppelbuchung: gleicher Kunde, gleiche Strecke, Abholzeit ±15 Min. wie ${row.board.duplicates.map(shortId).join(', ')}`;
    default: return code;
  }
}

export const tariffText = (tariff) => {
  if (!tariff) return null;
  if (tariff.version === 'unreadable') return 'Tarifnachweis gerade nicht lesbar · Details prüfen';
  if (tariff.version !== 'pulse-essen-admin-receipt-v1') return null;
  return `Essen · ${tariff.receipt_status === 'recorded' ? 'Betriebseingang nachgewiesen' : 'Betriebseingang noch offen'}${tariff.receipt_review_required || tariff.review_open ? ' · bitte prüfen' : ''}`;
};

function dayLabel(key, todayKey, tomorrowKey, sample, yesterdayKey) {
  const name = tagUeberschrift(sample, { wochentag: true });
  if (key === todayKey) return `Heute · ${name}`;
  if (key === tomorrowKey) return `Morgen · ${name}`;
  if (key === yesterdayKey) return `Gestern · ${name}`;
  return name;
}

const groupUnits = (rows) => {
  const units = new Map();
  for (const r of rows) {
    const key = r.bundle_id || r.id;
    if (!units.has(key)) units.set(key, []);
    units.get(key).push(r);
  }
  return [...units.values()];
};

function Drawer({ title, subtitle, onClose, children }) {
  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="resv-drawer-backdrop" onClick={onClose}>
      <aside className="resv-drawer" role="dialog" aria-label={title} onClick={(event) => event.stopPropagation()}>
        <header>
          <div><h3>{title}</h3>{subtitle && <p>{subtitle}</p>}</div>
          <button type="button" className="resv-btn" onClick={onClose} aria-label="Schließen"><X size={14} /> Schließen</button>
        </header>
        {children}
      </aside>
    </div>
  );
}

function DriverDrawer({ row, onClose, onDone }) {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [after, setAfter] = useState(null);
  const [back, setBack] = useState([]);
  const [page, setPage] = useState({ drivers: [], next: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const receipt = useRef(null);
  useEffect(() => { const t = setTimeout(() => { setQuery(search.trim()); setAfter(null); setBack([]); }, 300); return () => clearTimeout(t); }, [search]);
  useEffect(() => {
    let live = true;
    setLoading(true);
    api.post('/admin/reservation-inbox/driver-candidates', { ride_id: row.id, ...(after ? { after } : {}), ...(query ? { search: query } : {}) })
      .then((r) => { if (live) { setPage({ drivers: r.data.drivers || [], next: r.data.next || null }); setError(''); } })
      .catch((e) => { if (live) setError(reasons[e.response?.data?.error] || 'Fahrerauswahl konnte nicht geladen werden.'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [row.id, query, after]);
  const drivers = [...page.drivers].sort((a, b) => Number(b.eligible) - Number(a.eligible) || Number(b.online) - Number(a.online));
  const assign = async (driver) => {
    if (saving) return;
    if (!window.confirm(`Reservierung ${shortId(row.id)} direkt ${driver.name} (${driver.partner_name}) zuweisen?\n\nDer Fahrer muss in seiner App annehmen. Nimmt er nicht an, geht sie an alle Fahrer seines Partners. Bestehende Partner- und Fahrerzuweisungen werden aufgehoben. Buchung und Zahlung bleiben erhalten.`)) return;
    const body = { ride_id: row.id, driver_id: driver.id, expected_state: row.expected_state };
    const binding = JSON.stringify(body);
    if (receipt.current?.binding !== binding) receipt.current = { binding, id: globalThis.crypto.randomUUID() };
    setSaving(true); setError('');
    try {
      const r = await api.post('/admin/reservation-inbox/assign-driver', { ...body, request_id: receipt.current.id });
      receipt.current = null;
      onDone(`Fahrerangebot an ${driver.name} (${driver.partner_name})${r.data?.expiresAt ? ` · Annahme bis ${datumUndZeit(r.data.expiresAt, { jahr: false })}` : ''}. Der Fahrer muss noch annehmen.`);
    } catch (e) {
      const d = e.response?.data;
      setError(d?.error ? `${d.message || reasons[d.error] || d.error}. Die Reservierung wurde nicht zugewiesen.` : 'Antwort nicht bestätigt. Erneut versuchen verwendet dieselbe Anfrage.');
    } finally { setSaving(false); }
  };
  return (
    <Drawer title="Fahrer zuweisen" subtitle={`${row.customer_name} · ${datumUndZeit(row.planned_for, { jahr: false })} · ${row.pickup_address}`} onClose={onClose}>
      <label className="resv-drawer-search"><Search size={14} /><input aria-label="Fahrer suchen" placeholder="Fahrer oder Partner suchen …" maxLength={120} value={search} onChange={(e) => setSearch(e.target.value)} /></label>
      {error && <p className="resv-error" role="alert">{error}</p>}
      {loading ? <p className="resv-muted">Lädt …</p> : !drivers.length ? <p className="resv-muted">Keine Fahrer gefunden.</p> : (
        <ul className="resv-drv-list">
          {drivers.map((d) => (
            <li key={d.id} className={`resv-drv${d.eligible ? '' : ' is-no'}`}>
              <div>
                <span className={`resv-dot${d.online ? '' : ' is-off'}`} aria-hidden="true" />
                <b>{d.name}</b>{d.current && <span className="resv-tag">aktuell</span>}
                <div className="resv-muted">{d.partner_name} · {d.online ? 'online' : 'offline'}{d.eligible ? '' : ` · ${reasons[d.reason] || d.reason}`}</div>
              </div>
              {d.eligible
                ? <button type="button" className="resv-btn is-primary" disabled={saving} onClick={() => assign(d)}>{saving ? '…' : 'Zuweisen'}</button>
                : <span className="resv-muted">nicht möglich</span>}
            </li>
          ))}
        </ul>
      )}
      <div className="resv-drawer-pager">
        <button type="button" className="resv-btn" disabled={loading || !back.length} onClick={() => { setAfter(back.at(-1)); setBack((old) => old.slice(0, -1)); }}>Vorherige</button>
        <button type="button" className="resv-btn" disabled={loading || !page.next} onClick={() => { setBack((old) => [...old, after]); setAfter(page.next); }}>Weitere Fahrer</button>
      </div>
      <p className="resv-muted">Offline ist kein Grund. Der Fahrer muss in seiner App annehmen; bis dahin steht die Reservierung unter „Wartet auf Annahme“. Ab 45 Min. vor Abholung prüft das System zusätzlich die Anfahrt.</p>
    </Drawer>
  );
}

function PartnerDrawer({ unit, onClose, onDone }) {
  const [partners, setPartners] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const receipt = useRef(null);
  const ids = useMemo(() => unit.map((r) => r.id).sort(), [unit]);
  useEffect(() => {
    let live = true;
    api.post('/admin/reservation-inbox/candidates', { ride_ids: ids })
      .then((r) => { if (live) setPartners(r.data.partners || []); })
      .catch((e) => { if (live) { setPartners([]); setError(reasons[e.response?.data?.error] || 'Partnerauswahl konnte nicht geladen werden.'); } });
    return () => { live = false; };
  }, [ids]);
  const offer = async (partner) => {
    if (saving) return;
    if (!window.confirm(`${ids.length === 1 ? 'Reservierung' : `${ids.length} zusammengehörige Reservierungen`} an ${partner.name} anbieten?\n\nDer Partner muss annehmen. Bestehende Partner- und Fahrerzuweisungen werden aufgehoben. Buchung und Zahlung bleiben erhalten.`)) return;
    const body = { ride_ids: ids, partner_id: partner.id, expected_states: Object.fromEntries(unit.map((r) => [r.id, r.expected_state])) };
    const binding = JSON.stringify(body);
    if (receipt.current?.binding !== binding) receipt.current = { binding, id: globalThis.crypto.randomUUID() };
    setSaving(true); setError('');
    try {
      await api.post('/admin/reservation-inbox/assign', { ...body, request_id: receipt.current.id });
      receipt.current = null;
      onDone(`Partnerangebot an ${partner.name} erstellt. Der Partner muss noch annehmen.`);
    } catch (e) {
      const d = e.response?.data;
      setError(d?.atomic ? `${reasons[d.error] || d.error}. Nichts wurde zugewiesen.` : 'Antwort nicht bestätigt. Erneut versuchen verwendet dieselbe Anfrage.');
    } finally { setSaving(false); }
  };
  const first = unit[0];
  return (
    <Drawer title="Partner anbieten" subtitle={`${first.customer_name} · ${datumUndZeit(first.planned_for, { jahr: false })}${unit.length > 1 ? ` · Kette mit ${unit.length} Reservierungen` : ''}`} onClose={onClose}>
      {error && <p className="resv-error" role="alert">{error}</p>}
      {partners == null ? <p className="resv-muted">Lädt …</p> : (
        <ul className="resv-drv-list">
          {[...partners].sort((a, b) => Number(b.eligible) - Number(a.eligible)).map((p) => (
            <li key={p.id} className={`resv-drv${p.eligible ? '' : ' is-no'}`}>
              <div><b>{p.name}</b>{!p.eligible && <div className="resv-muted">{reasons[p.reason] || p.reason}</div>}</div>
              {p.eligible
                ? <button type="button" className="resv-btn is-primary" disabled={saving} onClick={() => offer(p)}>{saving ? '…' : 'Anbieten'}</button>
                : <span className="resv-muted">nicht möglich</span>}
            </li>
          ))}
        </ul>
      )}
      <p className="resv-muted">Der Partner muss annehmen. Die Frist richtet sich nach dem Vorlauf (siehe „Steuerung“).</p>
    </Drawer>
  );
}

function ReservationCard({ unit, nowMs, rules, busy, onDriver, onPartner, onDetails, onOpenMarket, onRemind, onCancelDuplicate }) {
  return unit.map((r, index) => {
    const steps = r.board?.steps || [];
    const pickupMs = ms(r.planned_for);
    const assignable = r.status === 'scheduled' && !r.bundle_id && Number.isFinite(pickupMs)
      && pickupMs > nowMs + (Number(rules?.hard_gate_before_min) || 10) * 60000;
    const offerable = r.status === 'scheduled' && unit.every((x) => x.selectable);
    const driverLabel = r.driver_id ? 'Fahrer tauschen' : r.assigned_driver_id ? 'Anderen Fahrer wählen' : 'Fahrer zuweisen';
    const tariff = tariffText(r.reservation_tariff);
    // RESERVIERUNG-KOMPLETT-001 F2/F3/F4: Freigabe und Erinnerung entscheidet der Server (board.actions).
    const actions = r.board?.actions || {};
    return (
      <article key={r.id} className={`resv-card is-${r.board?.category || 'wait'}`} data-reservation={r.id}>
        <div className="resv-when">
          <b>{uhrzeit(r.planned_for, { leer: '–' })}</b>
          <small>{r.board?.category === 'done' ? datumUndZeit(r.planned_for, { jahr: false }) : inText(r.planned_for, nowMs)}</small>
          {r.bundle_id && index === 0 && <span className="resv-tag">Kette · {unit.length}</span>}
        </div>
        <div className="resv-body">
          <div className="resv-who">{r.customer_name} <span className="resv-muted">· {CLASSES[r.ride_type] || r.ride_type} · {money(r.price)} · {METHODS[r.payment_method] || r.payment_method || 'Zahlart ?'}{r.payment_status ? `, ${PAYMENTS[r.payment_status] || r.payment_status}` : ''} · {shortId(r.id)}</span></div>
          <div className="resv-route">{r.pickup_address} → {r.dropoff_address}</div>
          <ol className="resv-steps" aria-label="Ablauf">
            {steps.map((s) => <li key={s.key} className={`resv-step is-${s.state}`}><span aria-hidden="true">{ICON[s.state]}</span> {stepText(s, nowMs)}</li>)}
          </ol>
          {(r.board?.attention || []).map((code) => (
            <p key={code} className={code === 'possible_duplicate' ? 'resv-warn is-dup' : 'resv-warn'}>{attentionText(code, r, rules)}</p>
          ))}
          {r.board?.steps?.[2]?.kind === 'offered' && <p className="resv-hint">Erst wenn der Fahrer in seiner App annimmt, steht er fest.</p>}
          {tariff && <p className="resv-hint">{tariff}</p>}
        </div>
        <div className="resv-actions">
          {r.board?.category !== 'done' && (index === 0 || !r.bundle_id) && (
            <>
              {assignable && <button type="button" className={`resv-btn${r.board?.category === 'need' || r.board?.category === 'market' ? ' is-primary' : ''}`} onClick={() => onDriver(r)}>{driverLabel}</button>}
              {offerable && <button type="button" className="resv-btn" onClick={() => onPartner(unit)}>{r.partner_id || r.offered_partner_id ? 'Anderem Partner anbieten' : 'Partner anbieten'}</button>}
              {actions.open_market && <button type="button" className="resv-btn" disabled={busy} onClick={() => onOpenMarket(r)}>Für alle freigeben</button>}
              {actions.remind_driver && <button type="button" className="resv-btn" disabled={busy} onClick={() => onRemind(r)}>Fahrer erinnern</button>}
              {(r.board?.duplicates || []).length > 0 && r.status === 'scheduled' && (
                <button type="button" className="resv-btn is-danger" disabled={busy} onClick={() => onCancelDuplicate(r)}>Diese Buchung stornieren</button>
              )}
              {r.bundle_id && <p className="resv-muted">Kette: nur gemeinsam über einen Partner.</p>}
            </>
          )}
          <button type="button" className="resv-btn" onClick={() => onDetails(r.id)}>Details</button>
        </div>
      </article>
    );
  });
}

function RulesPanel({ rules }) {
  const [booking, setBooking] = useState(undefined);
  const [cities, setCities] = useState(undefined);
  useEffect(() => {
    let live = true;
    api.get('/admin/reservation-booking').then((r) => { if (live) setBooking(r.data); }).catch(() => { if (live) setBooking(null); });
    api.get('/admin/service-area').then((r) => { if (live) setCities(r.data?.cities || []); }).catch(() => { if (live) setCities(null); });
    return () => { live = false; };
  }, []);
  const leads = { over_24h: 'mehr als 24 Std. vorher', '3h_to_24h': '3–24 Std. vorher', '1h_to_3h': '1–3 Std. vorher', under_1h: 'unter 1 Std. vorher' };
  const fmtMin = (m) => (m >= 60 && m % 60 === 0 ? `${m / 60} Std.` : `${m} Min.`);
  const engine = <Link to="/engine" className="resv-tag is-today">in „Engine“ ändern</Link>;
  const fixed = <span className="resv-tag is-fixed">fest im Programm</span>;
  return (
    <section className="resv-panel" aria-label="Steuerung der Reservierungen">
      <h2>Steuerung der Reservierungen</h2>
      <p className="resv-muted">Alle Regeln an einem Ort. Schalter mit „Engine“ sind dort änderbar. Werte „fest im Programm“ werden hier nur angezeigt.</p>
      <div className="resv-rules">
        <div className="resv-rule"><div><b>Reservierungen annehmen</b>{engine}<p>Globaler Schalter für neue Reservierungen</p></div><span className="resv-val">{booking === undefined ? '…' : booking == null ? 'nicht lesbar' : booking.enabled ? 'An' : 'Aus'}</span></div>
        <div className="resv-rule"><div><b>Städte</b>{engine}<p>Wo Reservierungen buchbar sind</p></div><span className="resv-val is-list">{cities === undefined ? '…' : cities == null ? 'nicht lesbar' : cities.length ? cities.join(', ') : 'keine'}</span></div>
        <div className="resv-rule"><div><b>Für alle freigeben nach</b>{fixed}<p>Ohne Admin-Zuweisung geht sie an alle Partner und Fahrer</p></div><span className="resv-val">{rules?.open_market_after_min ?? '–'} Min.</span></div>
        <div className="resv-rule"><div><b>Spätestens vor Abholung freigeben</b>{fixed}<p>Auch wenn die {rules?.open_market_after_min ?? 30} Min. noch nicht um sind</p></div><span className="resv-val">{rules?.open_market_latest_before_min ?? '–'} Min.</span></div>
        <div className="resv-rule"><div><b>Frist für den Partner</b>{fixed}<p>{(rules?.partner_offer_windows_min || []).map((w) => `${leads[w.lead] || w.lead}: ${fmtMin(w.minutes)}`).join(' · ')}</p></div><span className="resv-val">nach Vorlauf</span></div>
        <div className="resv-rule"><div><b>Partner bzw. Fahrer hält sie bis</b>{fixed}<p>Danach sucht das System zusätzlich direkt</p></div><span className="resv-val">{rules?.driver_ready_before_min ?? '–'} Min. vorher</span></div>
        <div className="resv-rule"><div><b>Umwandlung in normale Fahrt</b>{fixed}<p>Ohne festen Fahrer spätestens dann normale Fahrtsuche</p></div><span className="resv-val">{rules?.hard_gate_before_min ?? '–'} Min. vorher</span></div>
        <div className="resv-rule"><div><b>Mögliche Doppelbuchung melden</b>{fixed}<p>Gleicher Kunde, gleiche Strecke, Abholung ±{rules?.duplicate_window_min ?? 15} Min. Nur Hinweis, keine Automatik.</p></div><span className="resv-val">An</span></div>
      </div>
    </section>
  );
}

export default function Reservations() {
  const navigate = useNavigate();
  const { refresh: refreshSummary } = useReservationAlert();
  const [data, setData] = useState({ rides: [], history: [], rules: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [tile, setTile] = useState('');
  const [range, setRange] = useState('all');
  const [search, setSearch] = useState('');
  const [drawer, setDrawer] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const generation = useRef(0);
  const busy = useRef(false);
  busy.current = !!drawer;

  const load = useCallback(async () => {
    const g = ++generation.current;
    try {
      const r = await api.get('/admin/reservation-inbox?view=board');
      if (g !== generation.current) return;
      setData({ rides: r.data.rides || [], history: r.data.history || [], rules: r.data.rules || null });
      setError('');
      setNowMs(Date.now());
    } catch {
      if (g === generation.current) setError('Reservierungen konnten nicht geladen werden.');
    } finally {
      if (g === generation.current) setLoading(false);
    }
  }, []);
  useEffect(() => { load(); return () => { generation.current += 1; }; }, [load]);
  useEffect(() => { const t = setInterval(() => { if (!busy.current) load(); }, POLL_MS); return () => clearInterval(t); }, [load]);

  const counts = useMemo(() => Object.fromEntries(TILES.map((t) => [t.key, data.rides.filter((r) => r.board?.category === t.key).length])), [data.rides]);
  const todayKey = tagSchluessel(nowMs);
  const tomorrowKey = tagSchluessel(nowMs + 86400000);
  const yesterdayKey = tagSchluessel(nowMs - 86400000);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.rides.filter((r) => {
      const category = r.board?.category;
      if (tile ? category !== tile : category === 'done') return false;
      if (range !== 'all') {
        const key = tagSchluessel(r.planned_for);
        if (range === 'today' && key !== todayKey) return false;
        if (range === 'tomorrow' && key !== tomorrowKey) return false;
        if (range === 'week' && !(ms(r.planned_for) < nowMs + 7 * 86400000)) return false;
      }
      if (!q) return true;
      return [r.id, r.customer_name, r.pickup_address, r.dropoff_address, r.partner_name, r.offered_partner_name,
        r.driver_name, r.assigned_driver_name].some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [data.rides, tile, range, search, todayKey, tomorrowKey, nowMs]);
  const days = useMemo(() => {
    const ordered = tile === 'done' ? [...visible].sort((a, b) => ms(b.planned_for) - ms(a.planned_for)) : visible;
    const map = new Map();
    for (const unit of groupUnits(ordered)) {
      const key = tagSchluessel(unit[0].planned_for) || 'ohne';
      if (!map.has(key)) map.set(key, { key, sample: unit[0].planned_for, units: [] });
      map.get(key).units.push(unit);
    }
    return [...map.values()];
  }, [visible, tile]);

  const done = (message) => { setDrawer(null); setNotice(message); load(); refreshSummary?.(); };
  // F2/F3/F4: Aktionen direkt an der Karte. Jede braucht eine Bestätigung; der Server prüft den Stand.
  const [busyAction, setBusyAction] = useState(false);
  const receipts = useRef(new Map());
  const requestIdFor = (binding) => {
    if (!receipts.current.has(binding)) receipts.current.set(binding, globalThis.crypto.randomUUID());
    return receipts.current.get(binding);
  };
  const runAction = async (fn, success) => {
    if (busyAction) return;
    setBusyAction(true); setError(''); setNotice('');
    try { await fn(); done(success); }
    catch (e) {
      const d = e.response?.data;
      const message = d?.message || ACTION_ERRORS[d?.error] || reasons[d?.error] || 'Aktion nicht bestätigt. Bitte aktualisieren und erneut versuchen.';
      await load();   // erst neu laden (setzt den Fehler zurück), dann den Grund zeigen
      setError(message);
    } finally { setBusyAction(false); }
  };
  const openMarket = (r) => {
    if (!window.confirm(`Reservierung ${shortId(r.id)} (${r.customer_name}, ${datumUndZeit(r.planned_for, { jahr: false })}) jetzt für alle Partner und Fahrer freigeben?\n\nWer zuerst annimmt, bekommt sie. Bestehende Partner- oder Fahrerangebote werden aufgehoben. Buchung und Zahlung bleiben erhalten.`)) return;
    const body = { ride_id: r.id, expected_state: r.expected_state };
    runAction(async () => {
      await api.post('/admin/reservation-inbox/open-market', { ...body, request_id: requestIdFor(`open:${JSON.stringify(body)}`) });
    }, 'Reservierung ist jetzt offen für alle Partner und Fahrer.');
  };
  const remind = (r) => {
    const name = r.board?.steps?.[2]?.name || 'den Fahrer';
    if (!window.confirm(`${name} an die Reservierung ${shortId(r.id)} erinnern?\n\nEr bekommt eine Push-Nachricht in der Fahrer-App. Höchstens einmal je 10 Minuten.`)) return;
    runAction(async () => {
      await api.post('/admin/reservation-inbox/remind-driver', { ride_id: r.id, expected_state: r.expected_state });
    }, `Erinnerung an ${name} gesendet.`);
  };
  const cancelDuplicate = (r) => {
    const others = (r.board?.duplicates || []).map(shortId).join(', ');
    if (!window.confirm(`Reservierung ${shortId(r.id)} von ${r.customer_name} als Doppelbuchung stornieren?\n\nDie andere Buchung (${others}) bleibt bestehen. Für den Kunden fällt keine Stornogebühr an. Das lässt sich nicht rückgängig machen.`)) return;
    runAction(async () => {
      await api.post(`/admin/rides/${encodeURIComponent(r.id)}/cancel`, { reason_code: 'other', cancel_reason: 'Doppelbuchung – vom Admin storniert' });
    }, `Doppelbuchung ${shortId(r.id)} storniert.`);
  };
  const openDetails = (id) => navigate(`/rides?ride=${encodeURIComponent(id)}`);

  return (
    <div className="resv-page">
      <PageHeader
        title="Reservierungen"
        description="Jede Reservierung zeigt in einer Zeile, wer schon angenommen hat und was noch fehlt. Die Knöpfe stehen direkt an der Reservierung."
        actions={<button type="button" className="resv-btn" onClick={load} disabled={loading}><RefreshCw size={14} /> Aktualisieren</button>}
      />
      <div className="resv-tiles" role="group" aria-label="Reservierungen nach Stand">
        {TILES.map((t) => (
          <button type="button" key={t.key} className={`resv-tile${t.key === 'need' && counts.need ? ' is-bad' : ''}${tile === t.key ? ' is-on' : ''}`}
            aria-pressed={tile === t.key} onClick={() => setTile(tile === t.key ? '' : t.key)}>
            <b>{counts[t.key] || 0}</b><span>{t.label}</span>
          </button>
        ))}
      </div>
      <div className="resv-bar">
        <label className="resv-search"><Search size={14} /><input aria-label="Reservierungen suchen" placeholder="Suchen: Kunde, Adresse, Fahrer, Partner, Nummer …" value={search} onChange={(e) => setSearch(e.target.value)} /></label>
        {[['all', 'Alle Tage'], ['today', 'Heute'], ['tomorrow', 'Morgen'], ['week', '7 Tage']].map(([key, label]) => (
          <button type="button" key={key} className={`resv-chip${range === key ? ' is-on' : ''}`} aria-pressed={range === key} onClick={() => setRange(key)}>{label}</button>
        ))}
        {tile && <button type="button" className="resv-chip" onClick={() => setTile('')}>Filter „{TILES.find((t) => t.key === tile)?.label}“ aufheben</button>}
      </div>
      {error && <p className="resv-error" role="alert">{error}</p>}
      {notice && <p className="resv-notice" role="status">{notice} <button type="button" className="resv-link" onClick={() => setNotice('')}>OK</button></p>}
      {loading ? <p className="resv-muted">Lädt …</p> : !days.length ? (
        <p className="resv-empty">{tile ? 'Keine Reservierungen in dieser Kachel.' : 'Keine offenen Reservierungen.'}</p>
      ) : days.map((day) => (
        <section key={day.key} className="resv-day" aria-label={dayLabel(day.key, todayKey, tomorrowKey, day.sample, yesterdayKey)}>
          <h2 className="resv-day-title">{dayLabel(day.key, todayKey, tomorrowKey, day.sample, yesterdayKey)}</h2>
          {day.units.map((unit) => (
            <div key={unit[0].bundle_id || unit[0].id} className={unit[0].bundle_id ? 'resv-unit is-bundle' : 'resv-unit'}>
              <ReservationCard unit={unit} nowMs={nowMs} rules={data.rules}
                onDriver={(row) => { setNotice(''); setDrawer({ type: 'driver', row }); }}
                onPartner={(u) => { setNotice(''); setDrawer({ type: 'partner', unit: u }); }}
                onDetails={openDetails} busy={busyAction}
                onOpenMarket={openMarket} onRemind={remind} onCancelDuplicate={cancelDuplicate} />
            </div>
          ))}
        </section>
      ))}
      <RulesPanel rules={data.rules} />
      <details className="resv-panel resv-journal">
        <summary>Zuweisungsjournal (letzte 30)</summary>
        <ul>
          {data.history.map((h) => (
            <li key={h.request_id}>
              {datumUndZeit(h.created_at_ms, { jahr: false })} · {h.opened_by_admin_name ? `Admin ${h.opened_by_admin_name}` : h.admin_name || (h.admin_id === 'system:open-market' ? 'System (30 Min. ohne Admin-Zuweisung oder Absage)' : h.admin_id === 'system:essen-partner' ? 'System (PULSE-Essen automatisch)' : String(h.admin_id || '').startsWith('system') ? 'System' : h.admin_id)}
              {' → '}{h.admin_id === 'system:open-market' ? 'offen für alle Partner und Fahrer' : h.driver_id ? `Fahrer ${h.driver_name || h.driver_id} (${h.partner_name})` : h.partner_name}
              {' · '}{(h.ride_ids || []).map(shortId).join(', ')}
              {h.transfer_from?.length ? ` · neu zugewiesen von ${[...new Set(h.transfer_from.map((x) => x.partner_name || x.partner_id || 'ohne Partner'))].join(', ')}` : ''}
            </li>
          ))}
        </ul>
      </details>
      {drawer?.type === 'driver' && <DriverDrawer row={drawer.row} onClose={() => setDrawer(null)} onDone={done} />}
      {drawer?.type === 'partner' && <PartnerDrawer unit={drawer.unit} onClose={() => setDrawer(null)} onDone={done} />}
    </div>
  );
}
